import type { ReactNode } from "react";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { StepDots } from "./primitives/StepDots";

interface OnboardingShellProps {
  children: ReactNode;
  /** Skip the back button on specific steps (Welcome has nothing to go back to). */
  showBack?: boolean;
  /** Hide the step dots on terminal screens (AllSet looks cleaner without). */
  showDots?: boolean;
}

/**
 * Persistent shell wrapping every step in the onboarding flow.
 *
 * Provides:
 *   - a top strip with the CoWork wordmark and an escape hatch,
 *   - the centered content card,
 *   - a footer with back button and step dots.
 *
 * The ``children`` slot receives a keyed wrapper with forward/backward
 * slide animations so every step entrance runs its own motion.
 */
export function OnboardingShell({
  children,
  showBack = true,
  showDots = true,
}: OnboardingShellProps) {
  const { step, goBack, transitionForward } = useOnboardingStore();

  const canGoBack = step !== "welcome" && step !== "done" && showBack;

  return (
    <div className="min-h-screen bg-gradient-to-br from-jove-10 via-surface to-jove-30">
      {/* Top strip */}
      <div className="flex items-center justify-between px-8 py-5">
        <div className="flex items-center gap-2 text-[14px] font-medium tracking-[-0.005em] text-space">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-on-primary">
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
              <circle cx="10" cy="10" r="3" />
              <path d="M10 2v3M10 15v3M2 10h3M15 10h3M4.6 4.6l2.1 2.1M13.3 13.3l2.1 2.1M15.4 4.6l-2.1 2.1M6.7 13.3l-2.1 2.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </span>
          OmniWerk
        </div>
        {/* Emergency exit — only visible after welcome */}
        {step !== "welcome" && step !== "done" && (
          <button
            onClick={() => {
              // Eject entirely — return to login / close onboarding by
              // clearing local auth. We intentionally don't call this
              // "Skip" because nothing here is skippable.
              if (confirm("Leave setup? You can come back to finish it any time.")) {
                // Fire a synthetic logout so the user's next launch
                // re-runs the flow from scratch.
                localStorage.removeItem("access_token");
                localStorage.removeItem("refresh_token");
                window.location.reload();
              }
            }}
            className="text-[12px] font-medium tracking-[0.04em] text-space-50 transition-colors hover:text-space"
          >
            Exit setup
          </button>
        )}
      </div>

      {/* Center card */}
      <div className="flex items-start justify-center px-4 pt-6 pb-24">
        <div className="w-full max-w-[560px] rounded-3xl bg-surface px-8 py-10 shadow-card sm:px-10 sm:py-12">
          <div
            key={step}
            className={
              transitionForward
                ? "onb-enter-forward"
                : "onb-enter-backward"
            }
          >
            {children}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="fixed inset-x-0 bottom-0 flex items-center justify-between px-8 py-5">
        <div className="w-24">
          {canGoBack && (
            <button
              onClick={goBack}
              className="inline-flex items-center gap-1 text-[14px] font-medium text-space-50 transition-colors hover:text-space"
            >
              <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4">
                <path
                  d="M12 15l-5-5 5-5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back
            </button>
          )}
        </div>
        <div className="flex justify-center">
          {showDots && <StepDots current={step} hide={["done"]} />}
        </div>
        <div className="w-24 text-right">{/* reserved for future actions */}</div>
      </div>
    </div>
  );
}
